import { VOICE_SAMPLE_RATE, pcm16HasSignal } from './voicePcm.mjs';
export { VOICE_SAMPLE_RATE, pcm16HasSignal } from './voicePcm.mjs';

// Both processors resample against the actual context rate. Requested sampleRate is not a guarantee.
const processors = `
class VoiceCapture extends AudioWorkletProcessor {
  constructor() {
    super(); this.phase = 0; this.previous = 0; this.used = 0; this.frame = new Int16Array(480);
  }
  process(inputs) {
    const channels = inputs[0];
    if (!channels || !channels.length) return true;
    for (let i = 0; i < channels[0].length; i++) {
      let sample = 0;
      for (const channel of channels) sample += channel[i] / channels.length;
      this.phase += ${VOICE_SAMPLE_RATE};
      while (this.phase >= sampleRate) {
        this.phase -= sampleRate;
        const fraction = 1 - this.phase / ${VOICE_SAMPLE_RATE};
        const value = Math.max(-1, Math.min(1, this.previous + (sample - this.previous) * fraction));
        this.frame[this.used++] = Math.round(value * (value < 0 ? 32768 : 32767));
        if (this.used === this.frame.length) {
          const buffer = this.frame.buffer;
          this.port.postMessage(buffer, [buffer]); this.frame = new Int16Array(480); this.used = 0;
        }
      }
      this.previous = sample;
    }
    return true;
  }
}
// Live audio reaches the browser with ordinary network gaps of up to ~300 ms; a shorter reserve runs dry mid-speech.
const PLAYBACK_PREBUFFER_MS = 500;
const PLAYBACK_FADE_MS = 5;
class VoicePlayback extends AudioWorkletProcessor {
  constructor() {
    super(); this.queue = []; this.offset = 0; this.phase = 0; this.item = null; this.count = 0;
    this.reported = 0; this.buffered = 0; this.speaking = false;
    this.playing = false; this.waited = 0; this.gain = 0;
    this.port.onmessage = e => {
      const message = e.data;
      if (message.type === 'audio') {
        if (this.buffered + message.samples.length > ${VOICE_SAMPLE_RATE} * 30) {
          this.port.postMessage({ error: 'overflow' }); return;
        }
        this.queue.push(message); this.buffered += message.samples.length;
      }
    };
  }
  process(_inputs, outputs) {
    const output = outputs[0][0];
    for (let i = 0; i < output.length; i++) {
      const head = this.queue[0]; if (!head) break;
      if (!this.playing) {
        // Bound the wait too: Live has no item-end marker, so short tails must still drain.
        this.waited++;
        if (this.buffered < ${VOICE_SAMPLE_RATE / 1000} * PLAYBACK_PREBUFFER_MS && this.waited < sampleRate * PLAYBACK_PREBUFFER_MS / 1000) continue;
        this.playing = true; this.waited = 0;
      }
      if (head.itemId !== this.item) {
        this.item = head.itemId; this.count = 0; this.reported = 0; this.phase = 0;
      }
      // Fade the remaining queue to silence before starvation. A late refill restores gain gradually.
      const fadeSamples = ${VOICE_SAMPLE_RATE / 1000} * PLAYBACK_FADE_MS;
      this.gain = Math.min(1, this.gain + ${VOICE_SAMPLE_RATE} / sampleRate / fadeSamples, (this.buffered - this.phase) / fadeSamples);
      output[i] = head.samples[this.offset] / 32768 * this.gain;
      this.phase += ${VOICE_SAMPLE_RATE} / sampleRate;
      while (this.phase >= 1 && this.queue.length) {
        this.phase -= 1; this.offset++; this.count++; this.buffered--;
        if (this.offset === this.queue[0].samples.length) { this.queue.shift(); this.offset = 0; }
      }
      if (!this.queue.length) { this.playing = false; this.gain = 0; }
      const speaking = head.hasSignal && this.queue.length > 0;
      if (speaking !== this.speaking || this.reported === 0 && this.count > 0 || this.count - this.reported >= 2400 || !this.queue.length) {
        this.reported = this.count; this.speaking = speaking;
        this.port.postMessage({ itemId: this.item, ms: Math.floor(this.count / ${VOICE_SAMPLE_RATE / 1000}), speaking });
      }
    }
    return true;
  }
}
registerProcessor('voice-capture', VoiceCapture);
registerProcessor('voice-playback', VoicePlayback);
`;

/** Prepare/resume in the original gesture; start alone acquires the microphone. */
function preparePipeline({ capture, played, failed, signal }) {
  signal.throwIfAborted();
  const context = new AudioContext({ sampleRate: VOICE_SAMPLE_RATE });
  let stream, source, input, output;
  let itemId = null;
  let stopped = false;
  let started = false;
  const stop = () => {
    stream?.getTracks().forEach(track => track.stop());
    if (stopped) return;
    stopped = true;
    signal.removeEventListener('abort', stop);
    if (input) { input.port.onmessage = null; input.port.close(); input.disconnect(); }
    if (output) { output.port.onmessage = null; output.port.close(); output.disconnect(); }
    source?.disconnect();
    if (context.state !== 'closed') void context.close().catch(failed);
  };
  signal.addEventListener('abort', stop, { once: true });
  // Observe a rejected resume immediately even when a short press never starts.
  let resume;
  try { resume = context.resume(); }
  catch (error) { stop(); throw error; }
  const activation = Promise.allSettled([resume]).then(([result]) => {
    if (result.status === 'rejected') { stop(); failed(); }
    return result;
  });
  const checkActive = () => {
    signal.throwIfAborted();
    if (stopped) throw new DOMException('Voice capture stopped', 'AbortError');
  };
  const start = async () => {
    let url;
    try {
      checkActive();
      if (started) throw new Error('Voice capture already started');
      started = true;
      // Explicit browser cleanup constraints preserve the existing call pipeline.
      const media = navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1,
        sampleRate: VOICE_SAMPLE_RATE, echoCancellation: true, noiseSuppression: true,
        autoGainControl: true, voiceIsolation: true }, video: false }).then(value => {
          stream = value;
          if (stopped) stream.getTracks().forEach(track => track.stop());
          return value;
        });
      const results = await Promise.allSettled([media, activation]);
      if (results[0].status === 'fulfilled') stream = results[0].value;
      else throw results[0].reason;
      const resumed = results[1].value;
      if (resumed.status === 'rejected') throw resumed.reason;
      checkActive();
      url = URL.createObjectURL(new Blob([processors], { type: 'text/javascript' }));
      await context.audioWorklet.addModule(url);
      checkActive();
      input = new AudioWorkletNode(context, 'voice-capture');
      input.port.onmessage = event => capture(event.data);
      source = context.createMediaStreamSource(stream);
      source.connect(input); input.connect(context.destination);
      if (played) {
        output = new AudioWorkletNode(context, 'voice-playback', { outputChannelCount: [1] });
        output.port.onmessage = event => {
          if (event.data.error) { failed(); return; }
          played(event.data.itemId, event.data.ms, event.data.speaking);
        };
        output.connect(context.destination);
      }
      return {
        stop,
        beginItem: id => { itemId = id; },
        play: buffer => {
          if (!itemId || buffer.byteLength === 0 || buffer.byteLength % 2) { failed(); return; }
          const samples = new Int16Array(buffer);
          output?.port.postMessage({ type: 'audio', itemId, samples,
            hasSignal: pcm16HasSignal(new Uint8Array(buffer)) }, [buffer]);
        },
      };
    } catch (error) { stop(); throw error; }
    finally { if (url) URL.revokeObjectURL(url); }
  };
  return { start, stop };
}

export async function createVoiceAudio(capture, played, failed, signal) {
  return preparePipeline({ capture, played, failed, signal }).start();
}
export function prepareVoiceCapture(capture, failed, signal) {
  const prepared = preparePipeline({ capture, failed, signal });
  return { stop: prepared.stop, start: async () => {
    const { stop } = await prepared.start();
    return { stop };
  } };
}
export async function createVoiceCapture(capture, failed, signal) {
  return prepareVoiceCapture(capture, failed, signal).start();
}
